import { useRef, type FC, type MouseEventHandler } from 'react';
import { graphql, useFragment } from 'react-relay';
import {
    BuyerProductTile,
    SRC_SET_SIZES_TILE,
    type ImageBackgroundColor,
} from 'dibs-buyer-product-tile/exports/BuyerProductTile.ts';
import { SharedFavoriteItem } from '../../components/global/FavoriteIcons/SharedFavoriteItem.tsx';
import { useClientState } from 'dibs-react-hooks/exports/useClientState.ts';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';
import { useFavoritesItem } from 'dibs-portfolio-folder/exports/useFavoritesItem.tsx';

import { type SharedRespRelatedItem_item$key } from './__generated__/SharedRespRelatedItem_item.graphql.ts';
import { type SharedRespRelatedItem_user$key } from './__generated__/SharedRespRelatedItem_user.graphql.ts';
import { type SharedRespRelatedItem_viewer$key } from './__generated__/SharedRespRelatedItem_viewer.graphql.ts';
import { type FavoritesProviderChildrenProps } from '../../components/global/FavoriteIcons/FavoritesProviderChildrenProps.ts';
import { useBuyerProductTileSize } from '../hooks/useBuyerProductTileSize.ts';

type Props = {
    item: SharedRespRelatedItem_item$key;
    user: SharedRespRelatedItem_user$key | null | undefined;
    viewer: SharedRespRelatedItem_viewer$key | null | undefined;
    index: number;
    currency: string;
    onClick?: MouseEventHandler;
    favorites: FavoritesProviderChildrenProps;
    lazyLoadStartIndex?: number;
    onContentVisible?: () => void;
    imageBackgroundColor?: ImageBackgroundColor;
};

export const SharedRespRelatedItem: FC<Props> = ({
    index,
    item: itemRef,
    onClick,
    user: userRef,
    viewer: viewerRef,
    favorites,
    currency,
    lazyLoadStartIndex = 0,
    onContentVisible,
    imageBackgroundColor,
}) => {
    const viewer = useFragment(
        graphql`
            fragment SharedRespRelatedItem_viewer on Viewer {
                ...BuyerProductTile_viewer
            }
        `,
        viewerRef
    );
    const item = useFragment(
        graphql`
            fragment SharedRespRelatedItem_item on Item
            @argumentDefinitions(priceBookName: { type: "String" }) {
                ...BuyerProductTile_item
                    @arguments(isTrade: $isTrade, priceBookName: $priceBookName, showSeller: false)
                ...SharedFavoriteItem_item
                ...useFavoritesItem_item
            }
        `,
        itemRef
    );
    const user = useFragment(
        graphql`
            fragment SharedRespRelatedItem_user on User {
                ...useFavoritesItem_user
            }
        `,
        userRef
    );

    const tileElementRef = useRef(null);
    const { isSsrOnlyBot = false } = useServerVarsContext();
    const {
        isFolderVisible,
        showFolderPlusIcon,
        onUnFavorited,
        onFolderActionStart,
        onFolderActionEnd,
        onMouseEnter,
        onMouseLeave,
    } = useFavoritesItem({
        item,
        user,
        applyMouseEvents: true,
        tileElementRef,
    });
    const isClient = useClientState();
    const tileSize = useBuyerProductTileSize();

    return (
        <BuyerProductTile
            viewer={viewer}
            itemSearch={null}
            item={item}
            user={null}
            afterDisplay={isClient}
            currency={currency}
            imageBackgroundColor={imageBackgroundColor}
            index={index}
            itemsPerRow={lazyLoadStartIndex}
            onClick={onClick}
            onContentVisible={onContentVisible}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            size={tileSize}
            srcSet={{ sizes: SRC_SET_SIZES_TILE }}
            favoritesComponent={
                <SharedFavoriteItem
                    item={item}
                    isFolderVisible={isFolderVisible}
                    showFolderPlusIcon={showFolderPlusIcon}
                    onUnFavorited={onUnFavorited}
                    onFolderActionStart={onFolderActionStart}
                    onFolderActionEnd={onFolderActionEnd}
                    fetchFolderOnMouseOver
                    favorites={favorites}
                    tileSize={tileSize}
                />
            }
            showPrice={isClient || isSsrOnlyBot}
            tileElementRef={tileElementRef}
        />
    );
};
